import {useEffect, useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {EntitySearchSelect} from '@/components/entity-search-select'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {useTags, type Tag} from '@/hooks/empresa/organizacion/use-tags'
import {useAlcanceSve, useFijarVigenciaSve, type VigenciaSve} from '@/hooks/empresa/organizacion/use-cifras'
import {translateApiError} from '@/lib/errors'
import {tope} from '@/lib/campos-texto'

/** Los límites llegan de `GET /cifras`, no se repiten en TypeScript. Vacío es inválido: la constancia promete
 * un SVE nombrado. */
function validateVigencia(
  positionId: string | null,
  meses: string,
  sveNombre: string,
  minimo: number,
  maximo: number
): string | null {
  if (!positionId) return 'cifras.sve.modal.cargoRequerido'
  if (!/^\d+$/.test(meses) || Number(meses) < minimo || Number(meses) > maximo) {
    return 'errors.cifraValorInvalido'
  }
  if (sveNombre.trim() === '') return 'cifras.sve.modal.nombreRequerido'
  return null
}

/** S-4 / E10 D8: el alcance se ve antes de confirmar y viaja en el guardado; si el cargo cambió, la API da 409. */
export function VigenciaSveModal({
  open,
  onOpenChange,
  businessId,
  vigencia,
  mesesMinimo,
  mesesMaximo,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  businessId: string
  vigencia: VigenciaSve | null
  mesesMinimo: number
  mesesMaximo: number
}) {
  const {t} = useTranslation()
  const {data: cargos} = useTags(businessId, 'positions', open)
  const fijar = useFijarVigenciaSve(businessId)
  const [positionId, setPositionId] = useState<string | null>(null)
  const [meses, setMeses] = useState('')
  const [sveNombre, setSveNombre] = useState('')
  const [error, setError] = useState<string | undefined>()
  const reset = fijar.reset

  useEffect(() => {
    if (!open) return
    setPositionId(vigencia?.position_id ?? null)
    setMeses(vigencia ? String(vigencia.meses) : '')
    setSveNombre(vigencia?.sve_nombre ?? '')
    setError(undefined)
    reset()
  }, [open, vigencia, reset])

  const mesesNumero =
    /^\d+$/.test(meses) && Number(meses) >= mesesMinimo && Number(meses) <= mesesMaximo ? Number(meses) : null
  const {data: alcance} = useAlcanceSve(businessId, positionId, mesesNumero)
  const cargoElegido = cargos?.items.find((cargo) => cargo.id === positionId) ?? null

  function revisar() {
    setError(validateVigencia(positionId, meses, sveNombre, mesesMinimo, mesesMaximo) ?? undefined)
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const hallazgo = validateVigencia(positionId, meses, sveNombre, mesesMinimo, mesesMaximo)
    setError(hallazgo ?? undefined)
    if (hallazgo || !positionId || mesesNumero === null) return
    if (!alcance) return setError('cifras.sve.modal.alcancePendiente')
    fijar.mutate(
      {positionId, meses: mesesNumero, sveNombre: sveNombre.trim(), alcanceVistoExamenes: alcance.examenes},
      {onSuccess: () => onOpenChange(false)}
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('cifras.sve.modal.title')}</DialogTitle>
          <DialogDescription>{t('cifras.sve.modal.descripcion')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label htmlFor="vigencia-sve-cargo">{t('cifras.sve.modal.cargo')}</Label>
            <EntitySearchSelect<Tag>
              id="vigencia-sve-cargo"
              testId="vigencia-sve-cargo"
              items={cargos?.items ?? []}
              value={cargoElegido}
              getKey={(cargo) => cargo.id}
              filterText={(cargo) => cargo.name}
              renderOption={(cargo) => cargo.name}
              renderTriggerValue={(cargo) => cargo.name}
              placeholder={t('cifras.sve.modal.cargoPlaceholder')}
              searchPlaceholder={t('cifras.sve.modal.cargoBuscar')}
              noResultsLabel={t('cifras.sve.modal.cargoSinResultados')}
              disabled={vigencia !== null}
              onPick={(cargo) => {
                setPositionId(cargo?.id ?? null)
                setError(undefined)
              }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="vigencia-sve-meses">{t('cifras.sve.modal.meses')}</Label>
            <Input
              id="vigencia-sve-meses"
              data-testid="vigencia-sve-meses"
              type="number"
              inputMode="numeric"
              min={mesesMinimo}
              max={mesesMaximo}
              value={meses}
              onChange={(e) => {
                setMeses(e.target.value)
                setError(undefined)
              }}
              onBlur={revisar}
              aria-invalid={!!error}
            />
            <p className="text-xs text-muted-foreground">
              {t('cifras.sve.modal.rango', {minimo: mesesMinimo, maximo: mesesMaximo})}
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="vigencia-sve-nombre">{t('cifras.sve.modal.nombre')}</Label>
            <Input
              id="vigencia-sve-nombre"
              data-testid="vigencia-sve-nombre"
              value={sveNombre}
              maxLength={tope('ORG')}
              placeholder={t('cifras.sve.modal.nombrePlaceholder')}
              onChange={(e) => {
                setSveNombre(e.target.value)
                setError(undefined)
              }}
              onBlur={revisar}
            />
          </div>
          <div className="notice-amber space-y-1 rounded-lg px-3 py-2" data-testid="vigencia-sve-alcance">
            <p className="text-xs font-medium">{t('cifras.sve.modal.alcanceTitulo')}</p>
            <p className="text-xs">
              {alcance
                ? t('cifras.sve.modal.alcance', {examenes: alcance.examenes, vencidos: alcance.vencidos_hoy})
                : t('cifras.sve.modal.alcancePendiente')}
            </p>
          </div>
          <FieldError error={error} />
          <p className="text-xs text-muted-foreground">{t('cifras.sve.modal.constancia')}</p>
          <FormDialogFooter
            error={fijar.isError ? translateApiError(fijar.error) : null}
            pending={fijar.isPending}
            submitLabel={t('cifras.sve.modal.guardar')}
            onCancel={() => onOpenChange(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}
